import React from 'react';
import ReactDOM from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { BrowserRouter } from 'react-router-dom';

import '@/i18n';
import App from './App';
import { bootstrapTenant } from '@/lib/bootstrap-tenant';
import { usePWAStore } from '@/stores/pwa';
import { useAuthStore } from '@/stores/auth';
import { startLanguageSync } from '@/i18n/sync';
import { isUserLanguage } from '@/i18n/languages';
import { TenantProvider } from '@/providers/TenantProvider';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { ConfirmDialogProvider } from '@xtreed/academy-shared/ui';
import './styles/globals.css';

// Auto-reload on chunk load failure (happens after deploy with new hashes)
window.addEventListener('error', (e) => {
  if (
    e.message?.includes('Failed to fetch dynamically imported module') ||
    e.message?.includes('Loading chunk') ||
    e.message?.includes('Loading CSS chunk')
  ) {
    const reloaded = sessionStorage.getItem('chunk-reload');
    if (!reloaded) {
      sessionStorage.setItem('chunk-reload', '1');
      window.location.reload();
    }
  }
});
window.addEventListener('unhandledrejection', (e) => {
  if (
    e.reason?.message?.includes('Failed to fetch dynamically imported module')
  ) {
    const reloaded = sessionStorage.getItem('chunk-reload');
    if (!reloaded) {
      sessionStorage.setItem('chunk-reload', '1');
      window.location.reload();
    }
  }
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function waitPWAHydration() {
  return new Promise<void>((resolve) => {
    if (usePWAStore.persist.hasHydrated()) {
      resolve();
      return;
    }
    const unsubscribe = usePWAStore.persist.onFinishHydration(() => {
      unsubscribe();
      resolve();
    });
  });
}

// Resolve o cliente e pinta a marca dele ANTES do primeiro render: montar
// antes faria o visitante ver a identidade padrao piscar na tela.
Promise.all([bootstrapTenant(), waitPWAHydration()]).then(async ([tenant]) => {
  if (import.meta.env.DEV) {
    await useAuthStore.persist.rehydrate();
  }
  usePWAStore
    .getState()
    .setTenantLanguage(
      isUserLanguage(tenant?.defaultLanguage) ? tenant.defaultLanguage : null,
    );
  startLanguageSync();
  ReactDOM.createRoot(document.getElementById('root')!).render(
    <React.StrictMode>
      <TenantProvider config={tenant}>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <ConfirmDialogProvider>
              <ErrorBoundary>
                <App />
              </ErrorBoundary>
            </ConfirmDialogProvider>
          </BrowserRouter>
          <ReactQueryDevtools initialIsOpen={false} />
        </QueryClientProvider>
      </TenantProvider>
    </React.StrictMode>,
  );
});
